import { useState, useEffect, useCallback } from 'react';
import {
  getTasks,
  getMyTasks,
  getCreatedTasks,
  getOverdueTasks,
  getUpcomingTasks
} from '../api/tasks';

/**
 * Custom hook for managing tasks
 * @param {Object} options - Configuration options
 * @param {string} options.type - Type of tasks to fetch: 'all', 'my', 'created', 'overdue', 'upcoming'
 * @param {Object} options.filters - Filter parameters
 * @param {boolean} options.autoFetch - Auto fetch on mount (default: true)
 */
export const useTask = (options = {}) => {
  const {
    type = 'all',
    filters = {},
    autoFetch = true
  } = options;

  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch tasks based on type
   */
  const fetchTasks = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = { ...filters, ...customFilters };
      let response;

      switch (type) {
        case 'my':
          response = await getMyTasks(params);
          break;
        case 'created':
          response = await getCreatedTasks(params);
          break;
        case 'overdue':
          response = await getOverdueTasks();
          break;
        case 'upcoming':
          response = await getUpcomingTasks(params.days || 7);
          break;
        case 'all':
        default:
          response = await getTasks(params);
          break;
      }

      if (response.success) {
        setTasks(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch tasks');
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [type, filters]);

  /**
   * Refetch tasks with current filters
   */
  const refetch = useCallback(() => {
    fetchTasks();
  }, [fetchTasks]);

  /**
   * Change page
   */
  const changePage = useCallback((newPage) => {
    fetchTasks({ page: newPage });
  }, [fetchTasks]);

  /**
   * Change limit
   */
  const changeLimit = useCallback((newLimit) => {
    fetchTasks({ limit: newLimit, page: 1 });
  }, [fetchTasks]);

  /**
   * Apply filters
   */
  const applyFilters = useCallback((newFilters) => {
    fetchTasks({ ...newFilters, page: 1 });
  }, [fetchTasks]);

  /**
   * Clear filters
   */
  const clearFilters = useCallback(() => {
    fetchTasks({ page: 1 });
  }, [fetchTasks]);

  // Auto fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchTasks();
    }
  }, [fetchTasks, autoFetch]);

  return {
    tasks,
    pagination,
    loading,
    error,
    fetchTasks,
    refetch,
    changePage,
    changeLimit,
    applyFilters,
    clearFilters
  };
};

export default useTask;
